import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ─── Entidade ────────────────────────────────────────────────────────────────

export interface RepresentanteLegal {
  nome?: string;
  cpf?: string;
  cargo?: string;
  [key: string]: unknown;
}

export interface Entidade {
  id: string;
  workspaceId: string;
  cnpj: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  enderecoCompleto: string | null;
  municipioUf: string | null;
  representanteLegal: RepresentanteLegal | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateEntidadeInput {
  cnpj: string;
  razaoSocial: string;
  nomeFantasia?: string;
  enderecoCompleto?: string;
  municipioUf?: string;
  representanteLegal?: RepresentanteLegal;
}

// ─── Acordo ──────────────────────────────────────────────────────────────────

export type AcordoTipo = 'COLABORACAO' | 'FOMENTO' | 'COOPERACAO' | 'AUXILIO' | 'SUBVENCAO';

export interface Acordo {
  id: string;
  workspaceId: string;
  entidadeId: string;
  tipo: AcordoTipo;
  numero: string;
  orgaoConcedente: string;
  objeto: string;
  valorTotal: string | null;
  valorRepasse: string | null;
  valorContrapartida: string | null;
  dataInicio: string | null;
  dataFim: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateAcordoInput {
  entidadeId: string;
  tipo: AcordoTipo;
  numero: string;
  orgaoConcedente: string;
  objeto: string;
  valorTotal?: string;
  valorRepasse?: string;
  valorContrapartida?: string;
  dataInicio?: string;
  dataFim?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function entidadesPath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/entidades`;
}

function acordosPath(workspaceId: string): string {
  return `/v1/workspaces/${workspaceId}/acordos`;
}

// ─── Hooks — Entidades ───────────────────────────────────────────────────────

export function useEntidades(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['entidades', workspaceId],
    queryFn: () => api.get<{ items: Entidade[] }>(entidadesPath(workspaceId!)),
  });
}

export function useCreateEntidade(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateEntidadeInput) =>
      api.post<Entidade>(entidadesPath(workspaceId!), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entidades', workspaceId] });
    },
  });
}

export function useDeleteEntidade(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`${entidadesPath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['entidades', workspaceId] });
    },
  });
}

// ─── Hooks — Acordos ─────────────────────────────────────────────────────────

export function useAcordos(workspaceId: string | null | undefined) {
  return useQuery({
    enabled: !!workspaceId,
    queryKey: ['acordos', workspaceId],
    queryFn: () => api.get<{ items: Acordo[] }>(acordosPath(workspaceId!)),
  });
}

export function useCreateAcordo(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAcordoInput) =>
      api.post<Acordo>(acordosPath(workspaceId!), input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acordos', workspaceId] });
    },
  });
}

export function useDeleteAcordo(workspaceId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ ok: boolean }>(`${acordosPath(workspaceId!)}/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['acordos', workspaceId] });
    },
  });
}
